import { useState, useEffect } from 'react';
import { UserProfile } from '../../types';
import { Expert } from '../../types';
import { Trash2, Square, CheckSquare, Edit2 } from 'lucide-react';

interface Props {
    currentUser: UserProfile;
    expertsList: Expert[];
    onDataNeededRefresh: () => void;
}

interface EditingCell {
    id: number;
    field: string;
}

interface EditingPopup {
    id: number;
    field: 'description' | 'bio' | 'style' | 'field';
    content: string;
}

const TextEditPopup: React.FC<{
    field: string;
    content: string;
    onSave: (newContent: string) => void;
    onClose: () => void;
}> = ({ field, content, onSave, onClose }) => {
    const [text, setText] = useState(content);

    return (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center animate-fade-in">
            <div className="bg-mystic-bg border border-gray-700 p-6 rounded-lg w-full max-w-2xl">
                <h3 className="capitalize font-bold text-white mb-4">Edit {field}</h3>
                <textarea 
                    value={text} 
                    onChange={e => setText(e.target.value)}
                    className="w-full h-64 bg-mystic-inset border border-gray-600 rounded p-2 text-white font-mono text-xs"
                />
                <div className="flex justify-end gap-4 mt-4">
                    <button onClick={onClose} className="px-4 py-2 rounded bg-gray-700 hover:bg-gray-600 text-white font-bold text-xs">Cancel</button>
                    <button onClick={() => onSave(text)} className="px-4 py-2 rounded bg-mystic-accent hover:bg-mystic-accentHover text-black font-bold text-xs">Save</button>
                </div>
            </div>
        </div>
    );
};


const ExpertsTab: React.FC<Props> = ({ currentUser, expertsList: initialExpertsList, onDataNeededRefresh }) => {
    const [expertsList, setExpertsList] = useState(initialExpertsList);
    const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
    const [lastSelectedId, setLastSelectedId] = useState<number | null>(null);
    const [editingCell, setEditingCell] = useState<EditingCell | null>(null);
    const [tempCellValue, setTempCellValue] = useState<string | number | null>("");
    const [editingPopup, setEditingPopup] = useState<EditingPopup | null>(null);

    useEffect(() => {
        setExpertsList(initialExpertsList);
    }, [initialExpertsList]);
    
    const handleSelectAll = () => {
        if (expertsList.length > 0 && selectedIds.size === expertsList.length) {
            setSelectedIds(new Set());
        } else {
            const allIds = new Set(expertsList.map(item => item.rowid));
            setSelectedIds(allIds);
        }
    };

    const handleSelectRow = (id: number, e: React.MouseEvent) => {
        e.stopPropagation();
        const newSelected = new Set(selectedIds);
        
        if (e.shiftKey && lastSelectedId !== null) {
            const currentIndex = expertsList.findIndex(u => u.rowid === id);
            const lastIndex = expertsList.findIndex(u => u.rowid === lastSelectedId);
            const start = Math.min(currentIndex, lastIndex);
            const end = Math.max(currentIndex, lastIndex);
            for (let i = start; i <= end; i++) {
                if(expertsList[i].rowid) newSelected.add(expertsList[i].rowid);
            }
        } else {
            if (newSelected.has(id)) newSelected.delete(id);
            else newSelected.add(id);
        }

        setSelectedIds(newSelected);
        setLastSelectedId(id);
    };

    const handleBulkDelete = async () => {
        if (selectedIds.size === 0) return;
        if (!confirm(`Delete ${selectedIds.size} experts?`)) return;

        try {
            const res = await fetch('/api/cms/experts', {
                method: 'DELETE',
                headers: { 'Content-Type': 'application/json', 'x-admin-phone': currentUser.phone },
                body: JSON.stringify({ ids: Array.from(selectedIds) })
            });
            if (res.ok) {
                onDataNeededRefresh();
                setSelectedIds(new Set());
            }
        } catch (_e) { console.error(_e); }
    };

    const handleDeleteSingle = async (id: number) => {
        if (!confirm(`Delete expert #${id}?`)) return;
        try {
            await fetch('/api/cms/experts', {
                method: 'DELETE',
                headers: { 'Content-Type': 'application/json', 'x-admin-phone': currentUser.phone },
                body: JSON.stringify({ ids: [id] })
            });
            onDataNeededRefresh();
        } catch (_e) { console.error(_e); }
    };

    const handleCellClick = (item: Expert, field: string) => {
        const nonEditableFields = ['actions', 'rowid', 'avatar', 'description', 'bio', 'style', 'field'];
        if (!item.rowid || nonEditableFields.includes(field)) return;
        
        setEditingCell({ id: item.rowid, field });
        const value = item[field as keyof Expert];
        setTempCellValue(value === null || value === undefined ? '' : String(value));
    };

    const handleCellBlur = async () => {
        if (!editingCell) return;
        
        const updatedList = expertsList.map(u => u.rowid === editingCell.id ? { ...u, [editingCell.field]: tempCellValue } : u);
        setExpertsList(updatedList);
        const prevEditingCell = editingCell;
        setEditingCell(null);

        try {
            const expertToUpdate = updatedList.find(u => u.rowid === prevEditingCell.id);
            if (!expertToUpdate) return;
            
            // Special handling for fields that need to update the users table
            if (prevEditingCell.field === 'user_name' || prevEditingCell.field === 'last_active') {
                if (!expertToUpdate.user_id) {
                    console.error(`Cannot update ${prevEditingCell.field} without a user_id`);
                    onDataNeededRefresh();
                    return;
                }
    
                const fieldToUpdateInUsersTable = prevEditingCell.field === 'user_name' ? 'name' : 'last_active';
    
                await fetch('/api/users', {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json', 'x-admin-phone': currentUser.phone },
                    body: JSON.stringify({ 
                        id: expertToUpdate.user_id, 
                        [fieldToUpdateInUsersTable]: tempCellValue 
                    })
                });
            } else {
                // Default handling for other editable fields in the experts table
                await fetch('/api/cms/experts', {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json', 'x-admin-phone': currentUser.phone },
                    body: JSON.stringify({ id: expertToUpdate.rowid, [prevEditingCell.field]: tempCellValue })
                });
            }
        } catch (_e) { console.error("Error saving cell", _e); onDataNeededRefresh(); }
    };

    const handlePopupSave = async (newContent: string) => {
        if (!editingPopup) return;
        
        // Optimistic update
        const updatedList = expertsList.map(exp => 
            exp.rowid === editingPopup.id ? { ...exp, [editingPopup.field]: newContent } : exp
        );
        setExpertsList(updatedList);
        
        try {
            await fetch('/api/cms/experts', {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json', 'x-admin-phone': currentUser.phone },
                body: JSON.stringify({ id: editingPopup.id, [editingPopup.field]: newContent })
            });
        } catch (e) {
            console.error("Popup save error", e);
            onDataNeededRefresh(); // Revert on error
        } finally {
            setEditingPopup(null);
        }
    };
    
    const columns = [
        { key: 'avatar', label: '', width: '40px' },
        { key: 'user_id', label: 'USER ID', width: '80px' },
        { key: 'user_name', label: 'USER NAME' },
        { key: 'rowid', label: 'EXPERT ID', width: '80px' }, 
        { key: 'name', label: 'EXPERT NAME' },
        { key: 'slug', label: 'SLUG' },
        { key: 'description', label: 'DESCRIPTION', width: '150px' },
        { key: 'bio', label: 'BIO', width: '150px' },
        { key: 'style', label: 'STYLE', width: '150px' },
        { key: 'field', label: 'FIELD (JSON)', width: '300px' },
        { key: 'since', label: 'SINCE' },
        { key: 'views', label: 'VIEWS' },
        { key: 'last_active', label: 'LAST ACTIVE', width: '200px' },
        { key: 'actions', label: 'ACTIONS', width: '60px' }
    ];

    return (
        <div className="w-full relative animate-fade-in">
            {editingPopup && (
                <TextEditPopup 
                    field={editingPopup.field}
                    content={editingPopup.content}
                    onSave={handlePopupSave}
                    onClose={() => setEditingPopup(null)}
                />
            )}
            {selectedIds.size > 0 && (
                <div className="mb-4 flex items-center gap-2 animate-fade-in bg-blue-900/30 border border-blue-700/50 rounded px-2 py-1 self-start">
                    <span className="text-xs text-blue-200">{selectedIds.size} selected</span>
                    <button onClick={handleBulkDelete} className="text-red-400 hover:text-red-300 p-1"><Trash2 className="w-4 h-4" /></button>
                </div>
            )}
            <div className="overflow-x-auto border border-gray-800 rounded-sm">
                <table className="w-full text-left border-collapse table-fixed">
                    <thead className="bg-mystic-box">
                        <tr className="border-b border-gray-800 text-gray-500 text-[10px] uppercase font-bold tracking-wider">
                            <th className="p-2 border-r border-gray-800 w-10 text-center">
                                <div onClick={handleSelectAll} className="cursor-pointer flex justify-center text-gray-500 hover:text-white">
                                    {expertsList.length > 0 && selectedIds.size === expertsList.length ? <CheckSquare className="w-4 h-4 text-blue-400" /> : <Square className="w-4 h-4" />}
                                </div>
                            </th>
                            {columns.map(col => <th key={col.key} className="p-2 border-r border-gray-800 last:border-r-0" style={{ width: col.width }}>{col.label}</th>)}
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-800 bg-mystic-bg">
                        {expertsList.map(expert => {
                            const isSelected = selectedIds.has(expert.rowid);
                            return (
                                <tr key={expert.rowid} className={`group hover:bg-mystic-btn ${isSelected ? 'bg-blue-900/10' : ''}`}>
                                    <td className="p-2 border-r border-gray-800 text-center">
                                        <div onClick={(e) => handleSelectRow(expert.rowid, e)} className="cursor-pointer flex justify-center">
                                            {isSelected ? <CheckSquare className="w-4 h-4 text-blue-400" /> : <Square className="w-4 h-4 text-gray-700 group-hover:text-gray-500" />}
                                        </div>
                                    </td>
                                    {columns.map(col => {
                                        const isEditing = editingCell?.id === expert.rowid && editingCell?.field === col.key;
                                        return (
                                            <td key={col.key} className="p-2 border-r border-gray-800 last:border-r-0 text-xs text-gray-300 relative h-9" onClick={() => handleCellClick(expert, col.key)}>
                                                {(() => {
                                                    if (col.key === 'avatar') {
                                                        return <div className="w-6 h-6 rounded-full bg-cover bg-center bg-gray-700" style={{ backgroundImage: `url(${expert.avatar_img})` }}></div>
                                                    }
                                                    if (col.key === 'actions') {
                                                        return <div className="text-center"><button onClick={() => handleDeleteSingle(expert.rowid)} className="text-gray-600 hover:text-red-400"><Trash2 className="w-4 h-4" /></button></div>
                                                    }
                                                    if (['description', 'bio', 'style'].includes(col.key)) {
                                                        const content = (expert as any)[col.key] || '';
                                                        return (
                                                            <div className="flex items-center justify-between gap-2 h-full">
                                                                <p className="text-xs text-gray-400 italic truncate pr-2" title={content}>{content || 'Not set'}</p>
                                                                <button onClick={(e) => { e.stopPropagation(); setEditingPopup({ id: expert.rowid, field: col.key as any, content: content }); }} className="text-gray-600 hover:text-mystic-accent p-1 opacity-0 group-hover:opacity-100 transition-opacity"><Edit2 className="w-3 h-3"/></button>
                                                            </div>
                                                        );
                                                    }
                                                    if (col.key === 'field') {
                                                        const content = (expert as any)[col.key] || '';
                                                        let fields: string[] = [];
                                                        try {
                                                            const parsed = JSON.parse(content);
                                                            if (Array.isArray(parsed)) fields = parsed.filter(f => typeof f === 'string');
                                                        } catch {}
                                                        
                                                        return (
                                                            <div className="flex items-center justify-between gap-2 h-full">
                                                                <div className="flex flex-wrap gap-1 py-1">
                                                                    {fields.length > 0 ? (
                                                                        fields.map((field, i) => (
                                                                            <span key={i} className="bg-gray-700 text-gray-300 text-[10px] px-2 py-0.5 rounded-full whitespace-nowrap">{field}</span>
                                                                        ))
                                                                    ) : (
                                                                        <span className="text-xs text-gray-600 italic">Not set</span>
                                                                    )}
                                                                </div>
                                                                <button onClick={(e) => { e.stopPropagation(); setEditingPopup({ id: expert.rowid, field: 'field', content: content }); }} className="text-gray-600 hover:text-mystic-accent p-1 opacity-0 group-hover:opacity-100 transition-opacity shrink-0"><Edit2 className="w-3 h-3"/></button>
                                                            </div>
                                                        );
                                                    }
                                                    if (isEditing) {
                                                        return <input type="text" value={String(tempCellValue)} onChange={(e) => setTempCellValue(e.target.value)} onBlur={handleCellBlur} autoFocus onKeyDown={e => e.key === 'Enter' && handleCellBlur()} className="absolute inset-0 w-full h-full bg-mystic-inset px-2 text-white outline-none focus:ring-1 focus:ring-mystic-accent/50 text-xs font-mono" />
                                                    }
                                                    return (
                                                         <div className={`w-full h-full flex items-center ${['rowid', 'user_id', 'views', 'since', 'last_active'].includes(col.key) ? 'font-mono' : ''} ${!['actions', 'rowid', 'avatar', 'description', 'bio', 'style', 'field'].includes(col.key) ? 'cursor-text' : ''}`}>
                                                            {(expert as any)[col.key]}
                                                        </div>
                                                    );
                                                })()}
                                            </td>
                                        )
                                    })}
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
            
        </div>
    );
};

export default ExpertsTab;